/**
 * The one section eyebrow + section headline treatment.
 *
 * Pure and React-free (type-only import) so the values are unit-testable —
 * Vitest runs `environment: 'node'` here and the repo has no React render
 * harness, so a pure module is the only place a typography contract can
 * actually be enforced rather than merely intended.
 *
 * WHY IT LIVES IN shared-ui AND NOT THE STOREFRONT: the reviews sections
 * (`reviews_feed`, `reviews_summary`) render from this package, and a package
 * cannot import the storefront's `@/lib` path — which is exactly why they were
 * the last two sections still on the old treatment. It is reached through its
 * OWN subpath (`@sifly/shared-ui/section-typography`), never the root barrel:
 * a Server Component importing the barrel registers every `'use client'`
 * module reachable through it as a client entry for that route (see
 * docs/reference/storefront.md → "Client bundle"). This file is constants and
 * pure functions, so a per-file subpath registers nothing.
 *
 * WHY THIS EXISTS: the eyebrow was styled independently at six call sites and
 * had drifted into three different treatments on a single page (measured on
 * Adventure Elevated's homepage, 2026-07-31):
 *
 *   - `cta_band`      — 12px / 400 / 0.6px tracking / accent-readable
 *   - SectionHeader   — 13px / 600 / 1.56px tracking / text-secondary (grey)
 *     (stack)
 *   - SectionHeader   — 16px / 500 / normal tracking / heading color
 *     (split)
 *
 * All three read as "small uppercase kicker" in isolation, which is exactly
 * why nobody caught it: the drift is only visible when two of them are on
 * screen at once. Route every eyebrow through `sectionEyebrowStyle` — do not
 * re-derive these values inline.
 */
import type { CSSProperties } from 'react'

/**
 * Section headline size. One ramp, deliberately not a `--text-*` step:
 * calibrated against the reference design (Anton at 64px desktop) that the
 * statement bands exist to reproduce, and shared so a headline can't be
 * "display" in one section kind and `--text-2xl` in the next.
 */
export const SECTION_HEADLINE_SIZE = 'clamp(2rem, 4vw + 0.75rem, 4rem)'
export const SECTION_HEADLINE_LEADING = 'var(--leading-display, 1.1)'

/**
 * Where the eyebrow is painted. This is NOT the same question as "is the
 * page dark" — `on_media` is a caption sitting on a photo behind a scrim,
 * where the surface tokens describe the section, not the pixels under the
 * text.
 */
export type EyebrowSurface = 'light' | 'dark' | 'on_media'

/**
 * On a light surface the eyebrow is an ACCENT, not muted text — that is the
 * whole visual job it does above a display headline. It deliberately does not
 * consult `--section-on-surface-muted`: that override exists to keep *body*
 * copy legible on a tinted band, and letting it win here is what greyed out
 * half the eyebrows on the page in the first place.
 */
const EYEBROW_COLOR: Record<EyebrowSurface, string> = {
  light: 'var(--color-accent-readable, var(--color-highlight, var(--color-primary)))',
  dark: 'var(--color-on-surface-inverse, #f8fafc)',
  on_media: 'rgba(255,255,255,0.85)',
}

export function eyebrowSurface(onDark?: boolean): EyebrowSurface {
  return onDark ? 'dark' : 'light'
}

/**
 * Tracking is `normal`, not the `--text-eyebrow-tracking` step. At 16px the
 * spaced-out variant reads as a widget label; unspaced it reads as a spoken
 * line introducing the headline, which is what the reference design does.
 * `text-transform` still comes from the token so a tenant can opt out of
 * uppercase brand-wide.
 */
export function sectionEyebrowStyle(surface: EyebrowSurface): CSSProperties {
  return {
    fontFamily: 'var(--brand-font-accent, var(--font-heading))',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    letterSpacing: 'normal',
    textTransform: 'var(--text-eyebrow-transform)' as CSSProperties['textTransform'],
    color: EYEBROW_COLOR[surface],
  }
}

/**
 * The casing token on its own, for primary headings that own their own size
 * ramp and therefore cannot call `sectionHeadlineStyle`.
 *
 * The HERO is why this is exported. Every section headline read the token, but
 * hero variants render their own display scale, so the hero was the single
 * primary heading the transform never reached — a tenant who opted into
 * uppercase got CAPS section headers sitting under a Title Case hero, and the
 * documented workaround was to TYPE the caps into the headline. That is the
 * damaging fix: the transform restyles a string while leaving it intact, but
 * typed caps put the shouting in the DOM, where `NEXT GENERATION EFOILS` costs
 * the `eFoils` brand casing for Google and for a screen reader permanently.
 *
 * Same token and same `none` platform default as every section headline, so a
 * tenant who has not opted in renders exactly as before.
 */
export const SECTION_HEADLINE_TRANSFORM =
  'var(--section-headline-transform)' as CSSProperties['textTransform']

/**
 * Section headline size + leading + casing. Family and weight stay with the
 * caller, which may be threading `--section-headline-family/-weight`
 * overrides.
 *
 * Casing comes from the token, never from the caller. The reference treatment
 * this ramp reproduces is `text-transform: uppercase` at 3–4rem Anton 400; we
 * ported the size, family, weight and leading but not the transform, and then
 * hardcoded `uppercase` in `cta_band` alone. That is how one page ended up
 * with CAPS statement bands sitting above Title Case section headers. The
 * platform default is `none` — a headline is a sentence, not an affordance,
 * so a tenant opts IN (`dealer_branding.heading_case`), the mirror image of
 * `--cta-text-transform`, where all-caps is the house style and the token
 * opts a brand out.
 */
export function sectionHeadlineStyle(color: string): CSSProperties {
  return {
    fontSize: SECTION_HEADLINE_SIZE,
    lineHeight: SECTION_HEADLINE_LEADING,
    textTransform: SECTION_HEADLINE_TRANSFORM,
    color,
  }
}
